import { AuthenticatedTemplate } from "@azure/msal-react";
import { useMsal } from "@azure/msal-react";
import { Container } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBrain,
  faDesktop,
  faCloud,
  faCreditCard,
} from "@fortawesome/free-solid-svg-icons";

import { SEO } from "../components/SEO";
import techGuyDomLogo from "../images/logos/TechGuyDomCropped.svg";
import blueskyLogo from "../images/logos/Bluesky.png";
import mastadonLogo from "../images/logos/Mastodon.png";
import remoteSupport from "../images/RemoteSupportWide.png";
import azureArchCert from "../images/certifications/Azure_Arch.png";
import ccnpCert from "../images/certifications/CCNP.jpg";
import mcseCloudCert from "../images/certifications/MCSE_Cloud_Platform.png";
import isacaCismCert from "../images/certifications/ISACA_CISM.png";
import isc2SscpCert from "../images/certifications/ISC2_SSCP.png";

export const Home = () => {
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();

  return (
    <>
      <SEO
        title="Your Go-To IT Solutions Expert"
        description="TechGuyDom delivers AI consulting, secure remote IT support, cloud architecture, and domain & hosting services—enterprise expertise without the enterprise overhead."
        path="/"
      />
      <AuthenticatedTemplate>
        {activeAccount ? (
          <Container>
            <div></div>
          </Container>
        ) : null}
      </AuthenticatedTemplate>

      {/* Hero Section */}
      <div className="hero-section">
        <img
          src={techGuyDomLogo}
          alt="TechGuyDom Logo"
          className="hero-logo"
        />
        <h1 className="hero-tagline">Your Go-To IT Solutions Expert</h1>
        <p className="hero-description">
          TechGuyDom bridges the gap between technology and business with
          tailored IT solutions designed to meet your unique needs. Whether
          you're a small business, an enterprise, or an individual—TGD has you
          covered.
        </p>
        <a
          href="https://techguydom.screenconnect.com"
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-primary hero-cta"
        >
          Get Remote Support
        </a>
      </div>

      {/* Services Cards */}
      <div className="container services-section">
        <div className="row g-4">
          <div className="col-md-3">
            <div className="card service-card h-100">
              <div className="card-body text-center">
                <FontAwesomeIcon icon={faBrain} className="service-icon" />
                <h5 className="card-title">AI Solutions</h5>
                <p className="card-text">
                  Agentic AI pipelines, predictive analytics, and responsible AI
                  governance—delivered at enterprise scale.
                </p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card service-card h-100">
              <div className="card-body text-center">
                <FontAwesomeIcon icon={faDesktop} className="service-icon" />
                <h5 className="card-title">Remote Support</h5>
                <p className="card-text">
                  Secure, encrypted remote access to diagnose issues and provide
                  real-time troubleshooting for your devices.
                </p>
                <a
                  href="https://techguydom.screenconnect.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-primary btn-sm"
                >
                  Connect Now
                </a>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card service-card h-100">
              <div className="card-body text-center">
                <FontAwesomeIcon icon={faCloud} className="service-icon" />
                <h5 className="card-title">Domain & Hosting</h5>
                <p className="card-text">
                  Affordable domain registration and premium hosting solutions
                  to establish and grow your online presence.
                </p>
                <a
                  href="https://services.dommicro.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-primary btn-sm"
                >
                  Learn More
                </a>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card service-card h-100">
              <div className="card-body text-center">
                <FontAwesomeIcon icon={faCreditCard} className="service-icon" />
                <h5 className="card-title">Make a Payment</h5>
                <p className="card-text">
                  Secure online payments anytime, anywhere. Fast, safe, and
                  hassle-free.
                </p>
                <a
                  href="https://square.link/u/2HNpYE7l"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-primary btn-sm"
                >
                  Pay Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Why TechGuyDom */}
      <div className="container-fluid why-section">
        <div className="container text-center">
          <h3 className="section-title">Why TechGuyDom?</h3>
          <p className="section-intro mb-5">
            Enterprise-grade expertise without the enterprise overhead.
            Independent, fast, and transparent.
          </p>
          <div className="row g-4">
            <div className="col-md-3">
              <div className="why-card">
                <div className="why-icon">🏆</div>
                <h6>Enterprise Experience</h6>
                <p>Senior-level IT experience across cloud, security, and infrastructure—applied directly to your problem.</p>
              </div>
            </div>
            <div className="col-md-3">
              <div className="why-card">
                <div className="why-icon">⚡</div>
                <h6>Fast Response</h6>
                <p>No ticketing bureaucracy. Real help, when you need it.</p>
              </div>
            </div>
            <div className="col-md-3">
              <div className="why-card">
                <div className="why-icon">🔒</div>
                <h6>Security-First</h6>
                <p>CISM and SSCP certified. Your data is safe in every engagement.</p>
              </div>
            </div>
            <div className="col-md-3">
              <div className="why-card">
                <div className="why-icon">💬</div>
                <h6>Honest Advice</h6>
                <p>No upsells, no lock-in. Just straightforward recommendations.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* AI Section */}
      <div className="container-fluid ai-section">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center">
              <h3 className="section-title">AI-Driven Solutions & Engineering</h3>
              <p className="section-intro">
                Harness the power of AI to transform the way your organization
                works. TechGuyDom has real-world experience building and
                deploying AI-powered solutions at scale—helping teams move
                faster, make smarter decisions, and stay ahead of a rapidly
                changing technology landscape.
              </p>
            </div>
          </div>
          <div className="row g-4 mt-2">
            <div className="col-md-4">
              <div className="ai-feature">
                <h6>Agentic AI Pipelines</h6>
                <p>
                  Automate code review, test generation, and release
                  documentation with LLM-powered workflows—significantlyreducing
                  time-to-delivery.
                </p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="ai-feature">
                <h6>AI-Powered Analytics</h6>
                <p>
                  Real-time anomaly detection and data-driven decision dashboards
                  embedded directly into your core platforms at global scale.
                </p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="ai-feature">
                <h6>Enterprise AI Governance</h6>
                <p>
                  Responsible AI policies, ethical guardrails, and compliance
                  frameworks to keep your AI deployments safe and aligned with
                  your business values.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Remote Support Detail */}
      <div className="container-fluid remote-support-section">
        <div className="container">
          <div className="row align-items-center g-4">
            <div className="col-md-6">
              <h3 className="section-title">Remote Support</h3>
              <p>
                Our remote support link allows us to securely access your
                device, diagnose issues, and provide real-time troubleshooting.
                With encrypted connections and fast response times, we ensure a
                seamless and secure support experience.
              </p>
              <a
                href="https://techguydom.screenconnect.com"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                Remote Support Link
              </a>
            </div>
            <div className="col-md-6 text-end">
              <img
                src={remoteSupport}
                alt="Remote Support"
                className="remote-support-img"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Certifications */}
      <div className="container section-block text-center">
        <h3 className="section-title">Certifications</h3>
        <div className="cert-badges">
          <img src={ccnpCert} alt="CCNP" className="cert-badge" style={{ maxHeight: "110px" }} />
          <img src={azureArchCert} alt="Azure Architect" className="cert-badge" style={{ maxHeight: "80px" }} />
          <img src={mcseCloudCert} alt="MCSE Cloud" className="cert-badge" style={{ maxHeight: "80px" }} />
          <img src={isacaCismCert} alt="ISACA CISM" className="cert-badge" style={{ maxHeight: "80px" }} />
          <img src={isc2SscpCert} alt="ISC2 SSCP" className="cert-badge" style={{ maxHeight: "80px" }} />
        </div>
      </div>

      {/* Connect Section */}
      <div className="container-fluid connect-section">
        <div className="container text-center">
          <h3 className="section-title">Stay Connected</h3>
          <p>
            Follow TechGuyDom on social media to stay up to date on what we're
            working on, the services we offer, and where we're headed next.
          </p>
          <p style={{ fontSize: "0.95rem", color: "#aab4c8" }}>
            No spam. No ads. Just real updates from a real tech professional.
          </p>
          <div className="social-links">
            <a
              href="https://bsky.app/profile/techguydom.com"
              target="_blank"
              rel="noopener noreferrer"
              className="social-link"
            >
              <img
                src={blueskyLogo}
                alt="Bluesky"
                style={{ maxHeight: "20px", marginRight: "8px" }}
              />
              Follow on Bluesky
            </a>
            <a
              href="https://twit.social/@TechGuyDom"
              target="_blank"
              rel="noopener noreferrer"
              className="social-link"
            >
              <img
                src={mastadonLogo}
                alt="Mastodon"
                style={{ maxHeight: "22px", marginRight: "8px" }}
              />
              Follow on Mastodon
            </a>
          </div>
        </div>
      </div>
    </>
  );
};
